import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/home/Hero";
import Marquee from "@/components/home/Marquee";
import Purposes from "@/components/home/Purposes";
import StandardSection from "@/components/home/StandardSection";
import LocationBlock from "@/components/home/LocationBlock";
import Reviews from "@/components/home/Reviews";
import SoldShowcase from "@/components/home/SoldShowcase";
import VehicleCard from "@/components/site/VehicleCard";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo, localBusinessSchema } from "@/hooks/useSeo";

export default function Home() {
  const site = useSite();
  const reviews = (Array.isArray(site.reviews) ? site.reviews : []).filter((r) => r?.text?.trim());
  const [featured, setFeatured] = useState([]);
  const [count, setCount] = useState(0);

  useSeo({
    title: "4x4 Dealer Cirencester | Used 4x4 & SUV Specialist in the Cotswolds | Cirencester 4x4",
    description: "Cirencester 4x4 is a specialist used 4x4 and SUV dealer in Cirencester, serving the Cotswolds. Land Rover, Defender, Range Rover and more — carefully selected and properly prepared.",
    path: "/",
    schema: localBusinessSchema(site),
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const all = Array.isArray(r?.data) ? r.data : [];
      setCount(all.length);
      const f = all.filter((v) => v.featured);
      setFeatured((f.length ? f : all).slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero stockCount={count} />
      <Marquee />
      <Intro />
      {featured.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8 lg:px-12 lg:pb-36" data-testid="featured-stock">
          <div className="flex flex-col gap-6 border-t border-stone pt-16 sm:flex-row sm:items-end sm:justify-between">
            <Reveal>
              <SectionLabel index="02">Featured stock</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">Recently prepared.</h2>
            </Reveal>
            <Link to="/stock" className="link-u self-start text-sm font-semibold uppercase tracking-[0.14em] text-forest sm:self-auto" data-testid="featured-view-all-top">View all stock</Link>
          </div>
          <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
          <div className="mt-16 flex justify-center">
            <Link to="/stock" className="btn btn-primary" data-testid="featured-view-all">View all stock <ArrowRight className="arrow h-4 w-4" /></Link>
          </div>
        </section>
      )}
      <Purposes />
      <StandardSection />
      <SellBand />
      <SoldShowcase />
      {reviews.length > 0 && <Reviews reviews={reviews} />}
      <LocationBlock index={reviews.length > 0 ? "07" : "06"} />
    </main>
  );
}

function Intro() {
  return (
    <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-36" data-testid="brand-intro">
      <Reveal className="lg:col-span-5">
        <SectionLabel index="01">Our approach</SectionLabel>
        <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-[3.6rem]">The right 4x4, without the usual uncertainty.</h2>
      </Reveal>
      <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
        <p className="font-serif text-2xl leading-snug text-forest sm:text-[1.7rem]">Buying a used 4x4 should not mean taking a gamble. We carefully select vehicles we would be happy to own ourselves, then inspect, prepare and present them properly.</p>
        <p className="mt-6 text-base leading-relaxed text-charcoal/80">From family SUVs and capable tow cars to Defenders, Land Cruisers and distinctive country vehicles, every car is chosen for its condition, history, specification and suitability.</p>
        <Link to="/the-cirencester-standard" className="btn btn-secondary mt-10" data-testid="intro-why-buy">Why buy from us <ArrowRight className="arrow h-4 w-4" /></Link>
      </Reveal>
    </section>
  );
}

function SellBand() {
  return (
    <section className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 pb-8 pt-28 sm:px-8 lg:grid-cols-12 lg:px-12 lg:pt-40" data-testid="sell-section">
      <Reveal className="img-zoom overflow-hidden lg:col-span-7">
        <img src="/img/premises_home.webp" alt="Converted Cotswold stone barn showroom at dusk with a green Defender inside and SUVs on the gravel" loading="lazy" className="aspect-[16/10] w-full object-cover" />
      </Reveal>
      <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.1}>
        <SectionLabel index="05">We buy 4x4s</SectionLabel>
        <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl">Selling your 4x4?</h2>
        <p className="mt-6 text-base leading-relaxed text-charcoal/80">We buy carefully selected 4x4s and SUVs directly from owners. If your vehicle fits our stock profile, we can offer a straightforward appraisal with no obligation.</p>
        <Link to="/sell-your-4x4" className="btn btn-primary mt-10" data-testid="sell-band-cta">Get a valuation <ArrowRight className="arrow h-4 w-4" /></Link>
        <p className="mt-5 font-serif text-xl italic text-forest">Part exchange welcome.</p>
      </Reveal>
    </section>
  );
}
